/**
 * 「这个商品顾客点得下去吗」—— 展示层的唯一判据。
 * The one predicate behind "can the customer actually order this product".
 *
 * owner 2026-08-10 裁定：**必选组一个选项都没有的商品，前端不展示。**
 *
 * 这一条与 utils/sectionLimit 的 §8 坏配置兜底是**同一件事的两半**，缺一不可：
 *
 *  - 坏配置兜底管的是「下限比上限还大」——算出来做不到，就塌成一个做得到的数
 *    （`lo = hi = N`），顾客能往下走。
 *  - 但 `N = max(0, 1) = 1` 对**一个选项都没有的必选组**救不了：屏上要顾客
 *    「至少选 1 样」，而那一组里一样都没有。**那不是文案问题，是这个商品
 *    根本卖不了** —— 所以正确的做法不是放它过去，是别把它摆出来。
 *
 * ── 为什么「选项全都看不见」也算数 ──────────────────────────────────
 *
 * `unShow` 的选项不会画到屏幕上（判据见 ./modSectionState 的 `isOptionHidden`，
 * 与三处模板里那句 `v-if="… && !item.unShow"` 逐字同源）。所以「5 个选项全是
 * `unShow`」与「一个选项都没有」在顾客眼里是同一件事：那一组是空的。
 * 本仓此前对这两种形状都是**放它过去**（六处 `!options.every(o => o.unShow)`
 * 逃生口，空数组的 `[].every()` 恒真也一并被它接住），结果是这个商品带着一个
 * 没人填的必选组进了购物车 —— 厨房收到的单子少一项顾客本该做的选择。
 *
 * ── 刻意**不**算进来的两种 ──────────────────────────────────────────
 *
 *  - **`disabled`（点不动 / 售罄）的选项照样算「有」**：那是一时的库存状态，
 *    不是「这一组是空的」。把它算进来会让一个热卖到暂时售罄的口味把整个商品
 *    从菜单上抹掉。
 *  - **推荐菜 / 套餐子品那种伪组不算**：它们是前端在商品详情页当场拼出来的
 *    （`id: -1` 或 `id: ''`），选项还是异步填进去的。判据 `isRenderedSection`
 *    与模板那道 `v-if="each.id && …"` 门逐字同源 —— 模板不画的东西，
 *    这里也不该拿它去决定一个商品能不能卖。
 *
 * A product carrying a required modification section with nothing pickable in it
 * cannot be ordered at all, so it must not be listed. Sold-out options still
 * count as "present"; front-end-synthesised pseudo sections do not count.
 */

import { isOptionHidden } from './modSectionState'

/**
 * 这一组会不会真的作为配菜组画出来。
 *
 * 判据与三处模板里那句 `v-if="each.id && …"` 逐字同源：前端当场拼出来的
 * 推荐菜组（`id: -1`）与套餐组（`id: -1` / `id: ''`）都拿不到真 id，
 * 它们的选项是异步填的，不能拿来判断一个商品能不能卖。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Boolean}
 */
const isRenderedSection = (mod) => !!(mod && mod.id) && !(mod.id < 0)

/**
 * 这是一个「必选，但一样都选不了」的组吗。
 * A required section with nothing the customer could possibly pick.
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Boolean}
 */
export const sectionHasNothingToPick = (mod) => {
  if (!isRenderedSection(mod)) return false
  if (mod.type !== 'required') return false
  const options = Array.isArray(mod.options) ? mod.options : []
  // 空数组也在内：`[].every()` 恒真，「一个选项都没有」与「全都看不见」同一件事。
  return options.every(isOptionHidden)
}

/**
 * 套餐里**躲不开的**子品（`oneProduct` 组）有没有一个选不了的必选配菜组。
 * Whether a mandatory combo item carries an unsatisfiable required section.
 *
 * 只看 `type === 'oneProduct'` 的组 —— 那种组的子品是**一定会进这一单的**，
 * 它的配菜组填不了，整个套餐就下不了单。
 *
 * **`type === 'required'` 的组刻意不看**：那是「从这一组里挑几样」，其中某一个
 * 子品配坏了，顾客换一个就行；把整个套餐藏掉是误伤。那种组「全都不可选」的情况
 * 另有一道门（venue-home 的 `controlProducts` 已经在查了）。
 *
 * ⚠️ 这条路**只有前端管得着**：owner 2026-08-10 裁定套餐子品的配菜校验不做后端
 * （管理端把子品配菜整个塞进 `products[].detail.modifications` 透传，后端在下单
 * 链路上看不见它们）。所以这里与 `verificationComboRequired` 是同一件事的两半：
 * 这里负责别把卖不了的套餐摆出来，那边负责已经摆出来的也加不进购物车。
 *
 * @param {Object} product 商品 / product
 * @returns {Boolean}
 */
const comboHasUnpickableRequiredSection = (product) => {
  const comboGroups = Array.isArray(product?.comboProducts) ? product.comboProducts : []
  return comboGroups.some(group => {
    if (group?.type !== 'oneProduct') return false
    const items = Array.isArray(group?.products) ? group.products : []
    return items.some(item => {
      const mods = Array.isArray(item?.detail?.modifications) ? item.detail.modifications : []
      return mods.some(sectionHasNothingToPick)
    })
  })
}

/**
 * 这个商品有没有一个「必选，但一样都选不了」的组 —— 有就**不展示**。
 * Whether the product carries a required section the customer can never satisfy.
 *
 * 两处一起看：商品**自己**的 `modifications`，以及套餐里躲不开的子品
 * （`oneProduct` 组）各自的 `modifications`。两处的字段后端在
 * `get-product-list` 与 `get-product` 上都下发。
 *
 * @param {Object} product 商品 / product
 * @returns {Boolean}
 */
export const productHasUnpickableRequiredSection = (product) => {
  const modifications = Array.isArray(product?.modifications) ? product.modifications : []
  if (modifications.some(sectionHasNothingToPick)) return true
  return comboHasUnpickableRequiredSection(product)
}
